import axios, {AxiosResponse} from 'axios'
import {ID, QueryState, Response} from '../../../../../_metronic/helpers'
import {
  OverviewRecentActivitiesQueryResponse,
  Project,
  ProjectCycleVew,
  ProjectsQueryResponse,
  ResponseProject,
} from './_models'
import {Stage} from '../../../stages/components/core/_models'
import {Task} from '../tasks-list/core/_models'
import { PaginatedResponse } from '../tasks-list/kanban/hooks/useGetKanbanTasks'

const API_URL = process.env.REACT_APP_THEME_API_URL
const PROJECT_URL = `${API_URL}/api/projetos`
const GET_PUBLIC_URL = `${API_URL}/api/public/project`
const GET_PROJECTS_URL = `${API_URL}/api/projetos`

const getProjects = (): Promise<{data: ProjectsQueryResponse; isEmptyResults: boolean}> => {
  return axios
    .get(GET_PROJECTS_URL)
    .then((d: AxiosResponse<{data: ProjectsQueryResponse; isEmptyResults: boolean}>) => d.data)
}

const getProjectsTask = (id: ID): Promise<ProjectsQueryResponse> => {
  return axios
    .get(`${PROJECT_URL}/${id}/moreprojects`)
    .then((d: AxiosResponse<ProjectsQueryResponse>) => d.data)
}

const getProjectsTaskByUserId = (): Promise<ProjectsQueryResponse> => {
  return axios
    .get(`${PROJECT_URL}/moreprojects-by-userid`)
    .then((d: AxiosResponse<ProjectsQueryResponse>) => d.data)
}

const getProjectsList = (
  query: string
): Promise<{data: ProjectsQueryResponse; isEmptyResults: boolean; totalProjectsInInstance?: number}> => {
  return axios
    .get(`${GET_PROJECTS_URL}?${query}`)
    .then((d: AxiosResponse<{data: ProjectsQueryResponse; isEmptyResults: boolean; totalProjectsInInstance?: number}>) => d.data)
}

/**
 * Lista uma página de projetos para select/autocomplete (ex.: integrações Trello/ClickUp/Planner).
 * Retorna array simples { id, name } para exibição em dropdown com busca.
 */
const getProjectsPageForSelect = (
  page: number,
  perPage: number,
  search: string
): Promise<Array<{id: number; name: string}>> => {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('per_page', String(perPage))
  if (search && search.trim() !== '') params.set('search', search.trim())
  return axios.get(`${GET_PROJECTS_URL}?${params.toString()}`).then((res) => {
    const body = res.data
    const inner = body?.data
    const list = Array.isArray(inner) ? inner : (inner?.data ?? [])
    return (list || []).map((p: {id?: number; name?: string}) => ({
      id: Number(p.id),
      name: p.name || '',
    }))
  })
}

const searchProject = (search: QueryState): Promise<ProjectsQueryResponse> => {
  return axios
    .post(`${PROJECT_URL}/search`, search)
    .then((d: AxiosResponse<ProjectsQueryResponse>) => d.data)
}

const searchProjectByFilterSelect = (status: ID): Promise<ProjectsQueryResponse> => {
  return axios
    .get(`${PROJECT_URL}/${status}/searchProjectByFilterSelect`)
    .then((d: AxiosResponse<ProjectsQueryResponse>) => d.data)
}

const getOverviewRecentActivities = (
  projectId: ID
): Promise<OverviewRecentActivitiesQueryResponse> => {
  return axios
    .get(`${PROJECT_URL}/${projectId}/getOverviewRecentActivities`)
    .then((d: AxiosResponse<OverviewRecentActivitiesQueryResponse>) => d.data)
}

const searchProjectByClient = (search: QueryState): Promise<ProjectsQueryResponse> => {
  return axios
    .post(`${PROJECT_URL}/search-by-client`, search)
    .then((d: AxiosResponse<ProjectsQueryResponse>) => d.data)
}

const getProjectById = (id: ID): Promise<Project> => {
  return axios
    .get(`${PROJECT_URL}/${id}`)
    .then((response: AxiosResponse<Response<Project>>) => response.data)
    .then((response: Response<Project>) => response.data)
}

const getProjectByPublicUrl = (id: string): Promise<Project> => {
  return axios
    .get(`${GET_PUBLIC_URL}/${id}`)
    .then((response: AxiosResponse<Response<Project>>) => response.data)
    .then((response: Response<Project>) => response.data)
}

const getProjectCycleView = (id: ID): Promise<ProjectCycleVew> => {
  return axios
    .get(`${PROJECT_URL}/${id}/cycle-view`)
    .then((response: AxiosResponse<Response<ProjectCycleVew>>) => response.data)
    .then((response: Response<ProjectCycleVew>) => response.data)
}

const getProjectByClientId = (id: string | undefined): Promise<ProjectsQueryResponse> => {
  return axios
    .get(`${PROJECT_URL}/${id}/byClient`)
    .then((response: AxiosResponse<ProjectsQueryResponse>) => response.data)
}

const createProject = (project: Project): Promise<ResponseProject | undefined> => {
  return axios
    .post(`${PROJECT_URL}/novo`, project)
    .then((response: AxiosResponse<ResponseProject>) => response.data)
}

type ProjectToTemplateResponse = {
  data: {project_template_id: number; name: string}
  message?: string
}

type ProjectCloneResponse = {
  data: {project_id: number; name: string}
  message?: string
}

const toProjectTemplate = (
  projectId: ID,
  options?: {include_open_tasks?: boolean; include_delivered_tasks?: boolean}
): Promise<ProjectToTemplateResponse> => {
  return axios
    .post(`${PROJECT_URL}/${projectId}/to-template`, {
      include_open_tasks: options?.include_open_tasks ?? true,
      include_delivered_tasks: options?.include_delivered_tasks ?? true,
    })
    .then((response: AxiosResponse<ProjectToTemplateResponse>) => response.data)
}

const cloneProject = (
  projectId: ID,
  name: string,
  clientId?: ID,
  options?: {include_open_tasks?: boolean; include_delivered_tasks?: boolean}
): Promise<ProjectCloneResponse> => {
  return axios
    .post(`${PROJECT_URL}/${projectId}/clone`, {
      name,
      client_id: clientId,
      include_open_tasks: options?.include_open_tasks ?? true,
      include_delivered_tasks: options?.include_delivered_tasks ?? true,
    })
    .then((response: AxiosResponse<ProjectCloneResponse>) => response.data)
}

const updateProject = (
  project: Partial<Project> & {cycleOption?: string; cycleId?: number}
): Promise<ResponseProject> => {
  return axios
    .put(`${PROJECT_URL}/${project.id}/editar`, project)
    .then((response: AxiosResponse<Response<ResponseProject>>) => response.data)
    .then((response: Response<ResponseProject>) => response.data)
}

const removeSharedDescriptionProject = (
  project: Partial<Project> & {cycleOption?: string; cycleId?: number}
): Promise<ResponseProject> => {
  return axios
    .post(`${PROJECT_URL}/${project.id}/deletar-documento`)
    .then((response: AxiosResponse<Response<ResponseProject>>) => response.data)
    .then((response: Response<ResponseProject>) => response.data)
}

const deleteProject = (projectId: ID): Promise<void> => {
  return axios.delete(`${PROJECT_URL}/${projectId}/excluir`).then(() => {})
}

const openProject = (projectId: ID): Promise<void> => {
  return axios.post(`${PROJECT_URL}/${projectId}/abrir`).then(() => {})
}

const transferProject = (projectId: ID, newClientId: ID): Promise<ResponseProject> => {
  return axios
    .post(`${PROJECT_URL}/${projectId}/transferir`, {new_client_id: newClientId})
    .then((response: AxiosResponse<ResponseProject>) => response.data)
}

const getStages = async ({projectId}: {projectId: number}): Promise<Stage[]> => {
  const {data} = await axios.get(`${PROJECT_URL}/${projectId}/stages`)
  return data
}

const linkStage = async ({
  projectId,
  stages,
}: {
  projectId: number
  stages: Stage[]
}): Promise<Stage[]> => {
  const {data} = await axios.post(`${PROJECT_URL}/${projectId}/link-stages`, {stages})
  return data
}

const unlinkStage = async ({
  projectId,
  stageId,
}: {
  projectId: number
  stageId: number
}): Promise<Stage[]> => {
    const {data} = await axios.delete(`${PROJECT_URL}/${projectId}/stages/${stageId}/unlink`)
    return data
}

const reorderStages = async ({
  projectId,
  stageIds,
}: {
  projectId: number
  stageIds: number[]
}): Promise<Stage[]> => {
  const {data} = await axios.put(`${PROJECT_URL}/${projectId}/stages/reorder`, {stageIds})
  return data
}

const getUnlinkedStages = async ({projectId}: {projectId: number}): Promise<Stage[]> => {
  const {data} = await axios.get(`${PROJECT_URL}/${projectId}/unlinked-stages`)
  return data
}

const getTasks = async ({
  projectId,
  stageId,
  page,
  filters,
}: {
  projectId: number
  stageId: number
  page?: number
  filters?: string
}): Promise<PaginatedResponse<Task>> => {
  const query = [`page=${page ?? 1}`]
  if (filters && filters.length > 0) {
    query.push(filters)
  }
  const {data} = await axios.get(
    `${PROJECT_URL}/${projectId}/stages/${stageId}/tasks?${query.join('&')}`
  )
  return data
}

export {
  getProjects,
  getProjectByClientId,
  searchProject,
  searchProjectByFilterSelect,
  searchProjectByClient,
  getOverviewRecentActivities,
  deleteProject,
  getProjectById,
  createProject,
  toProjectTemplate,
  cloneProject,
  updateProject,
  openProject,
  transferProject,
  getProjectsList,
  getProjectsPageForSelect,
  getProjectsTask,
  getProjectsTaskByUserId,
  getProjectCycleView,
  getProjectByPublicUrl,
  removeSharedDescriptionProject,
  getStages,
  getTasks,
  getUnlinkedStages,
  linkStage,
  unlinkStage,
  reorderStages,
}
